import { useState, useEffect } from 'react';
import {
  Stack,
  Title,
  Text,
  Group,
  Button,
  Paper,
  SegmentedControl,
  ThemeIcon,
  TextInput,
  Chip,
  Divider,
  Popover,
  ActionIcon,
} from '@mantine/core';
import {
  IconShoppingBag,
  IconArrowRight,
  IconArrowLeft,
  IconInfoCircle,
} from '@tabler/icons-react';
import { ProductPreferences } from '../../types/familyProfile';
import { HouseholdSegment, bySegment } from '../../utils/householdSegment';

type ProductPreferencesStepProps = {
  value: ProductPreferences;
  onChange: (value: ProductPreferences) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isCompleting?: boolean;
  householdSegment?: HouseholdSegment;
};

export const ProductPreferencesStep = ({
  value,
  onChange,
  onNext,
  onBack,
  isLastStep,
  isCompleting = false,
  householdSegment,
}: ProductPreferencesStepProps) => {
  const [newPreferredBrand, setNewPreferredBrand] = useState('');
  const [newAvoidBrand, setNewAvoidBrand] = useState('');
  const [pendingNext, setPendingNext] = useState(false);

  // Effect to call onNext after state has been updated
  useEffect(() => {
    if (pendingNext) {
      setPendingNext(false);
      onNext();
    }
  }, [value.specificBrands, value.avoidBrands, pendingNext, onNext]);

  const addPreferredBrand = () => {
    if (!newPreferredBrand.trim()) {
      return;
    }

    // Split by comma and process each brand
    const newBrands = newPreferredBrand
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => 
        // Apply title casing (capitalize first letter of each word)
        item.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
      )
      .filter(brand => {
        // Check if already exists in preferred brands (case-insensitive)
        const alreadyInPreferred = (value.specificBrands || []).some(
          existing => existing.toLowerCase() === brand.toLowerCase()
        );
        return !alreadyInPreferred;
      });

    if (newBrands.length === 0) {
      setNewPreferredBrand('');
      return;
    }

    const currentPreferred = value.specificBrands || [];
    const currentAvoid = value.avoidBrands || [];
    
    // Add to preferred brands
    const updatedPreferred = [...currentPreferred, ...newBrands];
    
    // Remove from avoid list if adding to preferred (case-insensitive)
    const updatedAvoid = currentAvoid.filter(avoid => 
      !newBrands.some(newBrand => newBrand.toLowerCase() === avoid.toLowerCase())
    );

    onChange({
      ...value,
      specificBrands: updatedPreferred,
      avoidBrands: updatedAvoid
    });
    setNewPreferredBrand('');
  };

  const removePreferredBrand = (brand: string) => {
    onChange({
      ...value,
      specificBrands: value.specificBrands?.filter(b => b !== brand) || []
    });
  };

  const addAvoidBrand = () => {
    if (!newAvoidBrand.trim()) {
      return;
    }

    // Split by comma and process each brand
    const newBrands = newAvoidBrand
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => 
        // Apply title casing (capitalize first letter of each word)
        item.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
      )
      .filter(brand => {
        // Check if already exists in avoid brands (case-insensitive)
        const alreadyInAvoid = (value.avoidBrands || []).some(
          existing => existing.toLowerCase() === brand.toLowerCase()
        );
        return !alreadyInAvoid;
      });

    if (newBrands.length === 0) {
      setNewAvoidBrand('');
      return;
    }

    const currentPreferred = value.specificBrands || [];
    const currentAvoid = value.avoidBrands || [];
    
    // Add to avoid brands
    const updatedAvoid = [...currentAvoid, ...newBrands];
    
    // Remove from preferred list if adding to avoid (case-insensitive)
    const updatedPreferred = currentPreferred.filter(preferred => 
      !newBrands.some(newBrand => newBrand.toLowerCase() === preferred.toLowerCase())
    );

    onChange({
      ...value,
      specificBrands: updatedPreferred,
      avoidBrands: updatedAvoid
    });
    setNewAvoidBrand('');
  };

  const removeAvoidBrand = (brand: string) => {
    onChange({
      ...value,
      avoidBrands: value.avoidBrands?.filter(b => b !== brand) || []
    });
  };

  const handleNext = () => {
    let updated = false;
    let newValue = { ...value };

    // Auto-add text from "Specific Brands You Prefer" input
    if (newPreferredBrand.trim()) {
      const newBrands = newPreferredBrand
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item =>
          item.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
        )
        .filter(brand => {
          const alreadyInPreferred = (newValue.specificBrands || []).some(
            existing => existing.toLowerCase() === brand.toLowerCase()
          );
          return !alreadyInPreferred;
        });

      if (newBrands.length > 0) {
        const currentAvoid = newValue.avoidBrands || [];
        newValue.specificBrands = [...(newValue.specificBrands || []), ...newBrands];
        newValue.avoidBrands = currentAvoid.filter(avoid =>
          !newBrands.some(newBrand => newBrand.toLowerCase() === avoid.toLowerCase())
        );
        updated = true;
      }
    }

    // Auto-add text from "Brands to Avoid" input
    if (newAvoidBrand.trim()) {
      const newBrands = newAvoidBrand
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item =>
          item.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
        )
        .filter(brand => {
          const alreadyInAvoid = (newValue.avoidBrands || []).some(
            existing => existing.toLowerCase() === brand.toLowerCase()
          );
          return !alreadyInAvoid;
        });

      if (newBrands.length > 0) {
        const currentPreferred = newValue.specificBrands || [];
        newValue.avoidBrands = [...(newValue.avoidBrands || []), ...newBrands];
        newValue.specificBrands = currentPreferred.filter(preferred =>
          !newBrands.some(newBrand => newBrand.toLowerCase() === preferred.toLowerCase())
        );
        updated = true;
      }
    }

    if (updated) {
      onChange(newValue);
      setPendingNext(true);
      return;
    }

    onNext();
  };

  const content = (
    <Stack gap="xl">
      {/* Brand Preference */}
      <Stack gap="md">
        <Text fw={600} size="lg">
          Do you have a brand preference?
        </Text>
        <Text size="sm" c="dimmed">
          We'll prioritize these types of products in your shopping lists
        </Text>
        
        {/* Mobile: Vertical */}
        <SegmentedControl
          value={value.brandPreference || 'no_preference'}
          onChange={(val) => onChange({ 
            ...value, 
            brandPreference: val as 'store' | 'name' | 'no_preference' 
          })}
          data={[
            { 
              label: 'No preference',
              value: 'no_preference' 
            },
            { 
              label: 'Store brands',
              value: 'store' 
            },
            { 
              label: 'Name brands',
              value: 'name' 
            },
          ]}
          orientation="vertical"
          size="md"
          color="orange"
          fullWidth
          hiddenFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />
        
        {/* Desktop: Horizontal */}
        <SegmentedControl
          value={value.brandPreference || 'no_preference'}
          onChange={(val) => onChange({ 
            ...value, 
            brandPreference: val as 'store' | 'name' | 'no_preference' 
          })}
          data={[
            { 
              label: 'No preference',
              value: 'no_preference' 
            },
            { 
              label: 'Store brands',
              value: 'store' 
            },
            { 
              label: 'Name brands',
              value: 'name' 
            },
          ]}
          orientation="horizontal"
          size="md"
          color="orange"
          visibleFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />
      </Stack>

      <Divider />

      {/* Organic Preference */}
      <Stack gap="md">
        <Text fw={600} size="lg">
          Do you prefer organic?
        </Text>
        <Text size="sm" c="dimmed">
          Choose your preference for organic products
        </Text>
        
        {/* Mobile: Vertical */}
        <SegmentedControl
          value={value.organicPreference || 'no_preference'}
          onChange={(val) => onChange({ 
            ...value, 
            organicPreference: val as 'prefer_organic' | 'no_preference' 
          })}
          data={[
            { 
              label: 'No preference',
              value: 'no_preference' 
            },
            { 
              label: 'Prefer organic',
              value: 'prefer_organic' 
            },
          ]}
          orientation="vertical"
          size="md"
          color="orange"
          fullWidth
          hiddenFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />
        
        {/* Desktop: Horizontal */}
        <SegmentedControl
          value={value.organicPreference || 'no_preference'}
          onChange={(val) => onChange({ 
            ...value, 
            organicPreference: val as 'prefer_organic' | 'no_preference' 
          })}
          data={[
            { 
              label: 'No preference',
              value: 'no_preference' 
            },
            { 
              label: 'Prefer organic',
              value: 'prefer_organic' 
            },
          ]}
          orientation="horizontal"
          size="md"
          color="orange"
          visibleFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />
      </Stack>

      <Divider />

      {/* FoodHealth Score Preference */}
      <Stack gap="md">
        {/* The info icon is inline inside the label, glued to the last word,
            so on narrow screens it wraps WITH "preference" instead of
            dropping to its own line below the text */}
        <Text fw={600} size="lg">
          FoodHealth Score{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            preference
            <Popover width={260} position="bottom" withArrow shadow="md">
              <Popover.Target>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="gray"
                  aria-label="What is FoodHealth Score?"
                  style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 6 }}
                >
                  <IconInfoCircle size={14} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <Text size="xs" c="gray.7" style={{ lineHeight: 1.4 }}>
                  The <a href="https://www.foodhealth.co" target="_blank" rel="noreferrer" style={{ color: 'var(--mantine-color-blue-6)' }}>FoodHealth Score</a> rates products from 0–100 based on nutritional quality. When enabled, we'll favor higher-scoring products when choosing between similar options.
                </Text>
              </Popover.Dropdown>
            </Popover>
          </span>
        </Text>
        <Text size="sm" c="dimmed">
          Would you like us to prioritize products with higher nutrition scores?
        </Text>

        {/* Mobile: Vertical */}
        <SegmentedControl
          value={value.foodHealthPreference || 'no_preference'}
          onChange={(val) => onChange({
            ...value,
            foodHealthPreference: val as 'prefer_higher' | 'no_preference'
          })}
          data={[
            { label: 'No preference', value: 'no_preference' },
            { label: 'Prefer higher scores', value: 'prefer_higher' }
          ]}
          orientation="vertical"
          size="md"
          color="orange"
          fullWidth
          hiddenFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />

        {/* Desktop: Horizontal */}
        <SegmentedControl
          value={value.foodHealthPreference || 'no_preference'}
          onChange={(val) => onChange({
            ...value,
            foodHealthPreference: val as 'prefer_higher' | 'no_preference'
          })}
          data={[
            { label: 'No preference', value: 'no_preference' },
            { label: 'Prefer higher scores', value: 'prefer_higher' }
          ]}
          orientation="horizontal"
          size="md"
          color="orange"
          visibleFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />
      </Stack>

      <Divider />

      {/* Price Preference */}
      <Stack gap="md">
        <Text fw={600} size="lg">
          Price range preference
        </Text>
        <Text size="sm" c="dimmed">
          What's your typical shopping budget preference?
        </Text>
        
        {/* Mobile: Vertical */}
        <SegmentedControl
          value={value.pricePreference || 'no_preference'}
          onChange={(val) => onChange({ 
            ...value, 
            pricePreference: val as 'value' | 'mid_range' | 'premium' | 'no_preference' 
          })}
          data={[
            { 
              label: 'No preference',
              value: 'no_preference' 
            },
            { 
              label: 'Value', 
              value: 'value' 
            },
            { 
              label: 'Mid-range',
              value: 'mid_range' 
            },
            { 
              label: 'Premium', 
              value: 'premium' 
            },
          ]}
          orientation="vertical"
          size="md"
          color="orange"
          fullWidth
          hiddenFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />
        
        {/* Desktop: Horizontal */}
        <SegmentedControl
          value={value.pricePreference || 'no_preference'}
          onChange={(val) => onChange({ 
            ...value, 
            pricePreference: val as 'value' | 'mid_range' | 'premium' | 'no_preference' 
          })}
          data={[
            { 
              label: 'No preference',
              value: 'no_preference' 
            },
            { 
              label: 'Value', 
              value: 'value' 
            },
            { 
              label: 'Mid-range',
              value: 'mid_range' 
            },
            { 
              label: 'Premium', 
              value: 'premium' 
            },
          ]}
          orientation="horizontal"
          size="md"
          color="orange"
          visibleFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />
      </Stack>

      <Divider />

      {/* Specific Preferred Brands */}
      <Stack gap="md">
        <Text fw={600} size="lg">
          Specific brands you prefer
        </Text>
        <Text size="sm" c="dimmed">
          Add any specific brands you like to see in your shopping lists
        </Text>
        
        <Group gap="xs">
          <TextInput
            placeholder="e.g. Heinz, Private Selection, Simple Truth"
            value={newPreferredBrand}
            onChange={(e) => setNewPreferredBrand(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addPreferredBrand();
              }
            }}
            style={{ flex: 1 }}
          />
          <Button 
            size="sm" 
            onClick={addPreferredBrand} 
            disabled={!newPreferredBrand.trim()} 
            color="green"
          >
            Add
          </Button>
        </Group>

        {value.specificBrands && value.specificBrands.length > 0 && (
          <Group gap="xs">
            {value.specificBrands.map((brand) => (
              <Chip
                key={brand}
                checked={true}
                onChange={() => removePreferredBrand(brand)}
                variant="filled"
                color="green"
                size="sm"
              >
                {brand}
              </Chip>
            ))}
          </Group>
        )}
      </Stack>

      <Divider />

      {/* Brands to Avoid */}
      <Stack gap="md">
        <Text fw={600} size="lg">
          Brands to avoid
        </Text>
        <Text size="sm" c="dimmed">
          Add any brands you prefer not to see in your shopping lists
        </Text>
        
        <Group gap="xs">
          <TextInput
            placeholder="e.g. Heinz, Private Selection, Simple Truth"
            value={newAvoidBrand}
            onChange={(e) => setNewAvoidBrand(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addAvoidBrand();
              }
            }}
            style={{ flex: 1 }}
          />
          <Button 
            size="sm" 
            onClick={addAvoidBrand} 
            disabled={!newAvoidBrand.trim()} 
            color="red"
          >
            Add
          </Button>
        </Group>

        {value.avoidBrands && value.avoidBrands.length > 0 && (
          <Group gap="xs">
            {value.avoidBrands.map((brand) => (
              <Chip
                key={brand}
                checked={true}
                onChange={() => removeAvoidBrand(brand)}
                variant="filled"
                color="red"
                size="sm"
              >
                {brand}
              </Chip>
            ))}
          </Group>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
        <Stack gap="md" ta="center" align="center">
          <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
            <IconShoppingBag size={40} />
          </ThemeIcon>
          <Stack gap="xs" align="center">
            <Title order={2} size="h1" fw={700} c="dark">
              Grocery preferences
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={500}>
              {bySegment(householdSegment, {
                family:
                  'Help us find the right products for your family by sharing your grocery shopping preferences',
                household:
                  'Help us find the right products for your household by sharing your grocery shopping preferences',
                solo: 'Help us find the right products for you by sharing your grocery shopping preferences',
              })}
            </Text>
          </Stack>
        </Stack>

      {/* Product Preferences */}
        <Paper 
          radius="lg" 
          p={{ base: 'md', sm: 'xl' }}
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            width: '100%'
          }}
        >
          {content}
        </Paper>

      {/* Navigation */}
        <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
          <Button 
            variant="subtle" 
            size="md"
            leftSection={<IconArrowLeft size={18} />}
            onClick={onBack}
            color="orange"
          >
            Back
          </Button>
          
          <Button
            size="md"
            rightSection={<IconArrowRight size={18} />}
            onClick={handleNext}
            color="orange"
            variant="filled"
            loading={isCompleting && isLastStep}
          >
            {isLastStep ? 'Finish' : 'Continue'}
          </Button>
        </Group>
    </Stack>
  );
};
